import React, { useState } from 'react';
import { toast } from 'react-toastify';
import api from '../../services/api';
import './Admin.css';

const Notifications = () => {
  const [notificationForm, setNotificationForm] = useState({
    title: '',
    message: '',
    type: 'announcement',
    targetRole: 'all',
    priority: 'normal'
  });
  const [sending, setSending] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNotificationForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSendNotification = async (e) => {
    e.preventDefault();

    if (!notificationForm.title || !notificationForm.message) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setSending(true);
      await api.post('/admin/notifications/send', notificationForm);
      toast.success('Notification sent successfully');

      // Reset form
      setNotificationForm({
        title: '',
        message: '',
        type: 'announcement',
        targetRole: 'all',
        priority: 'normal'
      });
    } catch (error) {
      console.error('Error sending notification:', error);
      toast.error('Failed to send notification');
    } finally {
      setSending(false);
    }
  };

  const handleBroadcast = async (type) => {
    const messages = {
      maintenance: {
        title: 'Scheduled Maintenance',
        message: 'The platform will undergo scheduled maintenance on [DATE] from [TIME] to [TIME]. Please save your work.'
      },
      update: {
        title: 'Platform Update',
        message: 'We have released new features and improvements. Check out what\'s new!'
      },
      welcome: {
        title: 'Welcome to HiringOpen',
        message: 'Thank you for joining our platform. Start exploring opportunities today!'
      }
    };

    if (window.confirm(`Send ${type} notification to all users?`)) {
      try {
        await api.post('/admin/notifications/broadcast', {
          ...messages[type],
          type: 'announcement'
        });
        toast.success(`${type} notification sent to all users`);
      } catch (error) {
        console.error('Error broadcasting notification:', error);
        toast.error('Failed to broadcast notification');
      }
    }
  };

  return (
    <div className="admin-page">
      <div className="page-header">
        <div>
          <h1>Notifications & Communication</h1>
          <p>Send announcements and notifications to users</p>
        </div>
      </div>

      {/* Send Notification Form */}
      <div className="notification-section">
        <div className="section-card">
          <h2>Send Custom Notification</h2>
          <form onSubmit={handleSendNotification} className="notification-form">
            <div className="form-row">
              <div className="form-group">
                <label>Notification Title *</label>
                <input
                  type="text"
                  name="title"
                  value={notificationForm.title}
                  onChange={handleInputChange}
                  placeholder="Enter notification title"
                  className="form-input"
                  required
                />
              </div>

              <div className="form-group">
                <label>Type</label>
                <select
                  name="type"
                  value={notificationForm.type}
                  onChange={handleInputChange}
                  className="form-select"
                >
                  <option value="announcement">Announcement</option>
                  <option value="alert">Alert</option>
                  <option value="info">Information</option>
                  <option value="warning">Warning</option>
                  <option value="success">Success</option>
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Target Audience</label>
                <select
                  name="targetRole"
                  value={notificationForm.targetRole}
                  onChange={handleInputChange}
                  className="form-select"
                >
                  <option value="all">All Users</option>
                  <option value="candidate">Candidates Only</option>
                  <option value="employer">Employers Only</option>
                  <option value="consultant">Consultants Only</option>
                  <option value="admin">Admins Only</option>
                </select>
              </div>

              <div className="form-group">
                <label>Priority</label>
                <select
                  name="priority"
                  value={notificationForm.priority}
                  onChange={handleInputChange}
                  className="form-select"
                >
                  <option value="low">Low</option>
                  <option value="normal">Normal</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label>Message *</label>
              <textarea
                name="message"
                value={notificationForm.message}
                onChange={handleInputChange}
                placeholder="Enter notification message"
                className="form-textarea"
                rows="2"
                required
              />
            </div>

            <div className="form-actions">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={sending}
              >
                {sending ? 'Sending...' : 'Send Notification'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Quick Broadcast Templates */}
      <div className="notification-section">
        <div className="section-card">
          <h2>Quick Broadcast Templates</h2>
          <p className="section-description">
            Send pre-configured notifications to all users with one click
          </p>

          <div className="broadcast-grid">
            <div className="broadcast-card">
              <div className="broadcast-icon">🔧</div>
              <h3>Maintenance Notice</h3>
              <p>Notify users about scheduled maintenance</p>
              <button
                className="btn btn-primary"
                onClick={() => handleBroadcast('maintenance')}
              >
                Send to All
              </button>
            </div>

            <div className="broadcast-card">
              <div className="broadcast-icon">🎉</div>
              <h3>Platform Update</h3>
              <p>Announce new features and improvements</p>
              <button
                className="btn btn-primary"
                onClick={() => handleBroadcast('update')}
              >
                Send to All
              </button>
            </div>

            <div className="broadcast-card">
              <div className="broadcast-icon">👋</div>
              <h3>Welcome Message</h3>
              <p>Send welcome message to new users</p>
              <button
                className="btn btn-primary"
                onClick={() => handleBroadcast('welcome')}
              >
                Send to All
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Email Campaign Section */}
      <div className="notification-section">
        <div className="section-card">
          <h2>Email Campaigns</h2>
          <p className="section-description">
            Manage email campaigns and newsletters
          </p>

          <div className="campaign-info">
            <div className="info-box">
              <h4>Campaign Statistics</h4>
              <ul>
                <li>Total Sent: <strong>0</strong></li>
                <li>Open Rate: <strong>0%</strong></li>
                <li>Click Rate: <strong>0%</strong></li>
              </ul>
            </div>

            <div className="info-box">
              <h4>Recent Campaigns</h4>
              <p className="text-muted">No campaigns sent yet</p>
            </div>
          </div>

          <button className="btn btn-outline">
            <strong>Create Email Campaign (Coming Soon)</strong>
          </button>
        </div>
      </div>

      {/* Notification Templates */}
      <div className="notification-section">
        <div className="section-card">
          <h2>Notification Templates</h2>
          <p className="section-description">
            Manage reusable notification templates
          </p>

          <div className="templates-list">
            <div className="template-item">
              <div className="template-info">
                <h4>Job Application Received</h4>
                <p>Notify employers when they receive a new application</p>
              </div>
              <button className="btn btn-sm btn-outline">Edit</button>
            </div>

            <div className="template-item">
              <div className="template-info">
                <h4>Job Approved</h4>
                <p>Notify employers when their job posting is approved</p>
              </div>
              <button className="btn btn-sm btn-outline">Edit</button>
            </div>

            <div className="template-item">
              <div className="template-info">
                <h4>Application Status Update</h4>
                <p>Notify candidates about application status changes</p>
              </div>
              <button className="btn btn-sm btn-outline">Edit</button>
            </div>
          </div>

          <button className="btn btn-outline">
            <strong>Add New Template</strong>
          </button>
        </div>
      </div>

      {/* Communication Logs */}
      <div className="notification-section">
        <div className="section-card">
          <h2>Communication Logs</h2>
          <p className="section-description">
            View history of sent notifications and emails
          </p>

          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Type</th>
                  <th>Title</th>
                  <th>Recipients</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td colSpan="5" className="text-center text-muted">
                    No communication logs available
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Notifications;
